/**
 * Thành phần giao diện dùng chung theo mẫu "Thông tin nguồn" (bản mẫu CMS):
 *   - `TruongLoc`   ô lọc có nhãn đặt TRÊN control (thanh lọc của trang danh sách);
 *   - `TheTag`      tag trạng thái kiểu Ant Design (ok, thường, đỏ, vàng, xanh, đặc);
 *   - `NhanChuaCoApi`, `DaiChuaCoApi`, `BangChuaCoDuLieu` đánh dấu phần giao diện
 *     mà backend CHƯA có API: hiện đúng bố cục của mẫu, không có dữ liệu giả.
 */
import type { ReactNode } from 'react';
import { vi } from '../ngon-ngu/vi';

export interface TruongLocProps {
  nhan: string;
  /** id của control bên trong, để nhãn trỏ đúng control. */
  htmlFor?: string;
  children: ReactNode;
}

export function TruongLoc({ nhan, htmlFor, children }: TruongLocProps): ReactNode {
  return (
    <div className="truong-loc">
      <label className="truong-loc__nhan" htmlFor={htmlFor}>
        {nhan}
      </label>
      {children}
    </div>
  );
}

export type KieuTag = 'ok' | 'thuong' | 'do' | 'vang' | 'xanh' | 'dac' | 'trung-tinh';

export function TheTag({
  kieu = 'trung-tinh',
  children,
}: {
  kieu?: KieuTag;
  children: ReactNode;
}): ReactNode {
  return <span className={`the-tag the-tag--${kieu}`}>{children}</span>;
}

/** Nhãn nhỏ "Chưa có API" — đặt cạnh mục menu, tiêu đề trang, nút chưa hoạt động. */
export function NhanChuaCoApi(): ReactNode {
  return (
    <span className="nhan-chua-co-api" title={vi.chuaCoApi.goiY}>
      {vi.chuaCoApi.nhan}
    </span>
  );
}

export interface DaiChuaCoApiProps {
  /** Mô tả API backend cần bổ sung, ví dụ "GET /api/v1/notifications". */
  canApi: readonly string[];
}

/** Dải cảnh báo đầu trang: phần giao diện này chưa nối dữ liệu vì backend thiếu API. */
export function DaiChuaCoApi({ canApi }: DaiChuaCoApiProps): ReactNode {
  return (
    <div className="dai-chua-co-api" role="note" data-vung="chua-co-api">
      <strong>{vi.chuaCoApi.tieuDe}</strong>
      <span>{vi.chuaCoApi.moTa}</span>
      <span>
        {vi.chuaCoApi.canBoSung}:{' '}
        {canApi.map((a, i) => (
          <code key={a}>
            {a}
            {i < canApi.length - 1 ? ', ' : ''}
          </code>
        ))}
      </span>
    </div>
  );
}

/** Bảng đúng các cột của mẫu, thân bảng rỗng "Trống" — dùng khi chưa có API. */
export function BangChuaCoDuLieu({ nhan, cot }: { nhan: string; cot: readonly string[] }): ReactNode {
  return (
    <>
      <table className="bang" aria-label={nhan}>
        <thead>
          <tr>
            {cot.map((c) => (
              <th key={c} scope="col">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={cot.length}>
              <div className="bang-trong">{vi.chuaCoApi.bangTrong}</div>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="bang__phan-trang">
        <span>{vi.chuaCoApi.tongKhongXacDinh}</span>
      </div>
    </>
  );
}
